import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessChangeLogsContext = createContext();

export const useBusinessChangeLogs = () => {
  const context = useContext(BusinessChangeLogsContext);
  if (!context) {
    throw new Error('useBusinessChangeLogs must be used within a BusinessChangeLogsProvider');
  }
  return context;
};

export const BusinessChangeLogsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for business change logs endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getChangeLogsBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/business-change-logs/api/v1/business-change-logs`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/business-change-logs/api/v1/business-change-logs`;
    } else {
      throw new Error('Invalid login type for business change logs access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access business change logs');
    }
  }, [loginType]);

  // Get change logs by registry number with pagination and filters
  const getChangeLogsByRegistryNumber = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.operationType) queryParams.append('operation_type', options.operationType);
      if (options.changedColumn) queryParams.append('changed_column', options.changedColumn);
      if (options.changedBy) queryParams.append('changed_by', options.changedBy);
      if (options.dateFrom) queryParams.append('date_from', options.dateFrom);
      if (options.dateTo) queryParams.append('date_to', options.dateTo);

      const baseUrl = getChangeLogsBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`;
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch change logs';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getChangeLogsBaseUrl]);

  // Get change log statistics by registry number
  const getChangeLogStats = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getChangeLogsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get change log statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getChangeLogsBaseUrl]);

  // Get change logs filtered by operation type
  const getChangeLogsByOperation = useCallback(async (registryNumber, operationType, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      operationType: operationType,
    };

    return await getChangeLogsByRegistryNumber(registryNumber, filterOptions);
  }, [getChangeLogsByRegistryNumber, validateAccess]);

  // Get change logs filtered by changed column
  const getChangeLogsByColumn = useCallback(async (registryNumber, changedColumn, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      changedColumn: changedColumn,
    };

    return await getChangeLogsByRegistryNumber(registryNumber, filterOptions);
  }, [getChangeLogsByRegistryNumber, validateAccess]);

  // Get change logs filtered by who made the change
  const getChangeLogsByUser = useCallback(async (registryNumber, changedBy, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      changedBy: changedBy,
    };

    return await getChangeLogsByRegistryNumber(registryNumber, filterOptions);
  }, [getChangeLogsByRegistryNumber, validateAccess]);

  // Get change logs by date range
  const getChangeLogsByDateRange = useCallback(async (registryNumber, dateFrom, dateTo, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      dateFrom: dateFrom,
      dateTo: dateTo,
    };

    return await getChangeLogsByRegistryNumber(registryNumber, filterOptions);
  }, [getChangeLogsByRegistryNumber, validateAccess]);

  // Get recent change logs (last 24 hours)
  const getRecentChangeLogs = useCallback(async (registryNumber, options = {}) => {
    validateAccess();

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    const filterOptions = {
      ...options,
      dateFrom: yesterday.toISOString().split('T')[0], // YYYY-MM-DD format
    };

    return await getChangeLogsByRegistryNumber(registryNumber, filterOptions);
  }, [getChangeLogsByRegistryNumber, validateAccess]);

  // Export change logs data (prepare data for CSV/Excel export)
  const exportChangeLogsData = useCallback(async (registryNumber, filters = {}) => {
    validateAccess();

    // Get all change logs without pagination for export
    const exportOptions = {
      ...filters,
      limit: 10000, // Large limit for export
      offset: 0,
    };

    const result = await getChangeLogsByRegistryNumber(registryNumber, exportOptions);
    
    // Transform data for export
    const exportData = result.change_logs.map(log => ({
      'Log ID': log.log_id,
      'Business ID': log.business_id,
      'Operation': log.operation_type,
      'Changed Column': log.changed_column || 'N/A',
      'Old Value': log.old_value || 'N/A',
      'New Value': log.new_value || 'N/A',
      'Changed At': new Date(log.changed_at).toLocaleString(),
      'Changed By': log.changed_by || 'System',
      'IP Address': log.ip_address || 'N/A',
    }));

    return {
      data: exportData,
      totalCount: result.total_count,
      registryNumber: registryNumber,
      filename: `change_logs_${registryNumber}_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getChangeLogsByRegistryNumber, validateAccess]);

  // Health check for business change logs service
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/business-change-logs/health`
        : `${API_BASE_URL}/admin/business-change-logs/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Validation helpers
  const validateChangeLogFilters = useCallback((filters) => {
    const errors = [];

    if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
      errors.push({ field: 'limit', message: 'Limit must be between 1 and 1000' });
    }

    if (filters.offset && filters.offset < 0) {
      errors.push({ field: 'offset', message: 'Offset must be non-negative' });
    }

    if (filters.dateFrom && filters.dateTo && new Date(filters.dateFrom) > new Date(filters.dateTo)) {
      errors.push({ field: 'dateRange', message: 'Date from must be before date to' });
    }

    if (filters.operationType && !['INSERT', 'UPDATE', 'DELETE'].includes(filters.operationType.toUpperCase())) {
      errors.push({ field: 'operationType', message: 'Operation type must be INSERT, UPDATE, or DELETE' });
    }

    return errors;
  }, []);

  // Format change log data for display
  const formatChangeLogData = useCallback((log) => {
    return {
      ...log,
      formattedChangedAt: new Date(log.changed_at).toLocaleString(),
      operationColor: log.operation_type === 'INSERT' ? 'green' :
                     log.operation_type === 'UPDATE' ? 'blue' :
                     log.operation_type === 'DELETE' ? 'red' : 'gray',
      hasValueChange: log.old_value !== null || log.new_value !== null,
      displayText: log.changed_column 
        ? `${log.operation_type}: ${log.changed_column}`
        : log.operation_type,
      changeDescription: log.old_value && log.new_value 
        ? `Changed from "${log.old_value}" to "${log.new_value}"`
        : log.new_value 
        ? `Set to "${log.new_value}"`
        : log.old_value 
        ? `Removed "${log.old_value}"`
        : 'No value change recorded',
    };
  }, []);

  // Get change log summary for a business
  const getChangeLogSummary = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const [stats, recentLogs] = await Promise.all([
        getChangeLogStats(registryNumber),
        getRecentChangeLogs(registryNumber, { limit: 5 })
      ]);

      return {
        registryNumber,
        totalLogs: stats.stats.total_logs,
        updateOperations: stats.stats.update_operations,
        insertOperations: stats.stats.insert_operations,
        deleteOperations: stats.stats.delete_operations,
        recentChanges: stats.stats.recent_changes,
        latestLogs: recentLogs.change_logs,
        accessLevel: loginType,
        hasActivity: stats.stats.total_logs > 0,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get change log summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getChangeLogStats, getRecentChangeLogs, validateAccess, loginType]);

  // Get unique columns that have been changed
  const getChangedColumns = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const result = await getChangeLogsByRegistryNumber(registryNumber, { limit: 1000 });
      const columns = [...new Set(result.change_logs
        .map(log => log.changed_column)
        .filter(column => column !== null && column !== undefined)
      )];
      
      return {
        columns,
        count: columns.length,
        registryNumber,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get changed columns';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getChangeLogsByRegistryNumber, validateAccess]);

  // Get current endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master' 
        ? `${API_BASE_URL}/master/business-change-logs/api/v1/business-change-logs`
        : `${API_BASE_URL}/admin/business-change-logs/api/v1/business-change-logs`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,
    
    // Core Actions
    getChangeLogsByRegistryNumber,
    getChangeLogStats,
    
    // Search and Filter Actions
    getChangeLogsByOperation,
    getChangeLogsByColumn,
    getChangeLogsByUser,
    getChangeLogsByDateRange,
    getRecentChangeLogs,
    
    // Utility Actions
    exportChangeLogsData,
    checkHealth,
    getChangeLogSummary,
    getChangedColumns,
    
    // Helpers
    clearError,
    validateChangeLogFilters,
    formatChangeLogData,
    getEndpointInfo,
    
    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasChangeLogsAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <BusinessChangeLogsContext.Provider value={value}>
      {children}
    </BusinessChangeLogsContext.Provider>
  );
};